import React, { useState, useEffect } from 'react';
import { Calendar, Mail, Building, ExternalLink, Eye, XCircle, AlertTriangle, CheckCircle, Globe, BarChart3, FileText } from 'lucide-react';

const EAAScanRequestsList = () => {
  const [scanRequests, setScanRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedScan, setSelectedScan] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadScanRequests();
  }, []);

  const loadScanRequests = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const response = await fetch('/api/eaa-scanner.php?action=get_all');
      const result = await response.json();
      
      if (result.success) {
        setScanRequests(result.data || []);
      } else {
        setError('Failed to load scan requests: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error loading scan requests:', err);
      setError('Failed to connect to API. Please check your server configuration.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = (scanRequest) => {
    setSelectedScan(scanRequest);
    setShowModal(true);
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const parseScanResults = (resultsJson) => {
    try {
      return resultsJson ? JSON.parse(resultsJson) : null;
    } catch {
      return null;
    }
  };

  const getComplianceScore = (results) => {
    if (!results || !results.violations) return 0;
    const violationCount = results.violations.length;
    return Math.max(0, 100 - (violationCount * 10));
  };

  const getComplianceScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'serious': return 'bg-orange-100 text-orange-800';
      case 'moderate': return 'bg-yellow-100 text-yellow-800';
      case 'minor': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getImpactIcon = (impact) => {
    switch (impact) {
      case 'critical': return <XCircle className="w-4 h-4" />;
      case 'serious': return <AlertTriangle className="w-4 h-4" />;
      case 'moderate': return <AlertTriangle className="w-4 h-4" />;
      case 'minor': return <AlertTriangle className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getDomainFromUrl = (url) => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        {/* <div>
          <h2 className="text-2xl font-bold text-gray-900">EAA Scan Requests</h2>
          <p className="text-gray-600">Manage accessibility scan requests and results</p>
        </div> */}
        <div className="flex items-center space-x-4">
          <button
            onClick={loadScanRequests}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Scan Requests List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Website
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Results
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {scanRequests.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    No scan requests found
                  </td>
                </tr>
              ) : (
                scanRequests.map((scanRequest) => {
                  const results = parseScanResults(scanRequest.scan_results);
                  const score = getComplianceScore(results);
                  
                  return (
                    <tr key={scanRequest.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {scanRequest.name}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <Mail className="w-4 h-4 mr-1" />
                              {scanRequest.email}
                            </div>
                            {scanRequest.company && (
                              <div className="text-sm text-gray-500 flex items-center">
                                <Building className="w-4 h-4 mr-1" />
                                {scanRequest.company}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center mb-1">
                            <Globe className="w-4 h-4 mr-1" />
                            <a
                              href={scanRequest.website_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800"
                            >
                              {getDomainFromUrl(scanRequest.website_url)}
                            </a>
                          </div>
                          <div className="text-xs text-gray-500">
                            {scanRequest.website_url}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {results ? (
                            <>
                              <div className="flex items-center mb-1">
                                <BarChart3 className="w-4 h-4 mr-1" />
                                <span className={`font-medium ${getComplianceScoreColor(score)}`}>
                                  {score}% Compliance
                                </span>
                              </div>
                              <div className="text-xs text-gray-500">
                                {results.violations ? results.violations.length : 0} issues found
                              </div>
                              <div className="text-xs text-gray-500">
                                {results.passes ? results.passes.length : 0} tests passed
                              </div>
                            </>
                          ) : (
                            <span className="text-gray-500">No results</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(scanRequest.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewDetails(scanRequest)}
                            className="text-blue-600 hover:text-blue-900 flex items-center"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Details Modal */}
      {showModal && selectedScan && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  EAA Scan Details
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <p className="text-sm text-gray-900">{selectedScan.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <p className="text-sm text-gray-900">
                      <a href={`mailto:${selectedScan.email}`} className="text-blue-600 hover:text-blue-800">
                        {selectedScan.email}
                      </a>
                    </p>
                  </div>
                  {selectedScan.company && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company
                      </label>
                      <p className="text-sm text-gray-900">{selectedScan.company}</p>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Website URL
                    </label>
                    <p className="text-sm text-gray-900">
                      <a
                        href={selectedScan.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 flex items-center"
                      >
                        {selectedScan.website_url}
                        <ExternalLink className="w-4 h-4 ml-1" />
                      </a>
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Scan Date
                    </label>
                    <p className="text-sm text-gray-900">{formatDate(selectedScan.created_at)}</p>
                  </div>
                </div>
              </div>

              {/* Scan Results */}
              {(() => {
                const results = parseScanResults(selectedScan.scan_results);
                if (!results) {
                  return (
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                      <p className="text-sm text-gray-500">No scan results available</p>
                    </div>
                  );
                }

                const score = getComplianceScore(results);
                const violations = results.violations || [];
                const passes = results.passes || [];

                return (
                  <div className="space-y-6">
                    {/* Summary */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Scan Summary</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center">
                            <BarChart3 className="w-8 h-8 text-blue-600 mr-3" />
                            <div>
                              <p className="text-sm font-medium text-blue-600">Compliance Score</p>
                              <p className={`text-2xl font-bold ${getComplianceScoreColor(score)}`}>
                                {score}%
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-center">
                            <XCircle className="w-8 h-8 text-red-600 mr-3" />
                            <div>
                              <p className="text-sm font-medium text-red-600">Issues Found</p>
                              <p className="text-2xl font-bold text-red-600">{violations.length}</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center">
                            <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
                            <div>
                              <p className="text-sm font-medium text-green-600">Tests Passed</p>
                              <p className="text-2xl font-bold text-green-600">{passes.length}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Violations */}
                    {violations.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-4">Accessibility Issues</h4>
                        <div className="space-y-4">
                          {violations.map((violation, index) => (
                            <div key={index} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getImpactColor(violation.impact)}`}>
                                    {getImpactIcon(violation.impact)}
                                    <span className="ml-1 capitalize">{violation.impact}</span>
                                  </span>
                                  <h5 className="ml-3 text-sm font-medium text-gray-900">
                                    {violation.description}
                                  </h5>
                                </div>
                                <span className="text-xs text-gray-500">
                                  {violation.nodes ? violation.nodes.length : 0} instances
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{violation.help}</p>
                              {violation.helpUrl && (
                                <a
                                  href={violation.helpUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                                >
                                  <ExternalLink className="w-4 h-4 mr-1" />
                                  Learn more
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Scan Details */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Scan Details</h4>
                      <div className="text-sm text-gray-600 space-y-1">
                        {results.timestamp && (
                          <p><strong>Timestamp:</strong> {new Date(results.timestamp).toLocaleString()}</p>
                        )}
                        {results.scanDuration && (
                          <p><strong>Duration:</strong> {results.scanDuration}</p>
                        )}
                        {results.title && (
                          <p><strong>Page Title:</strong> {results.title}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EAAScanRequestsList;